import { useEffect, useState } from 'react';
import type { ActivityRow } from '../../shared/api.ts';
import type { SlideRow } from '../lib/proposalActions.ts';
import FlipPills from './FlipPills.tsx';
import { toggleRow, applyRow, rejectRow, confirmRevertRow, cancelRowPreview } from '../lib/proposalActions.ts';
import { useFlipShowing } from '../lib/reviewFlip.ts';
import { ChevronLeft, ChevronRight } from '../lib/icons.tsx';

/**
 * The on-slide review bar. When the slide on the canvas is showing a live
 * review — a fresh AI proposal, or what a revert/re-apply would restore —
 * this floats over the preview and says so, with the flip and the decision
 * right where the user is looking. The Activity tab lists everything; this
 * answers "is what I'm looking at the real slide?" without leaving it.
 */
type ProposalBannerProps = {
  slug: string;
  /** A SlideRow, not a bare ActivityRow: this banner renders ON a slide, so a
   *  row without one could never reach it — and the review verbs it calls
   *  require the id. */
  row: SlideRow;
  /** Every proposal awaiting review, deck-ordered — drives the N-of-M stepper. */
  queue?: ActivityRow[];
  onJumpToSlide?: (slideId: string) => void;
  onChanged?: () => void;
  /** Hand the measured overflow to chat's "tighten to fit". */
  onTighten?: (detail: string) => void;
};

export default function ProposalBanner({ slug, row, queue, onJumpToSlide, onChanged, onTighten }: ProposalBannerProps) {
  const [busy, setBusy] = useState(false);
  // Which version is on the canvas — SHARED with the Activity row and the chat
  // card (lib/reviewFlip.js). A pending create has no second side to ask the
  // server about. This used to be private state that only this surface synced,
  // so the banner and the Activity row disagreed after a flip.
  const [showing] = useFlipShowing(slug, row.slideId, { seed: !row.pending });
  // Overflow on the version currently on screen, straight from the preview
  // (useOverflowCheck → 'rkh-slide-overflow'). The chat card offers Tighten to
  // fit, but the review happens HERE — a user watching the slide had to know to
  // go looking in the thread for the one action that fixes what they can see
  // (Jake, 2026-07-31). Same message, same handler, third button.
  const [overflow, setOverflow] = useState<{ detail: string; severity?: string } | null>(null);

  // A proposed NEW slide is under review IN the deck (llm/pending-slides.ts):
  // same decision, but there is no "before" to flip to — the alternative to
  // keeping it is that the slide isn't there at all.
  const review = row.pending
    ? 'create'
    : row.status === 'ready'
      ? 'proposal'
      : row.status === 'applied'
        ? 'revert'
        : row.status === 'reverted'
          ? 'reapply'
          : null;

  // Batch review: this row's position in the deck-ordered queue of proposals
  // awaiting review. With more than one, the banner grows a "2 of 5" stepper
  // and auto-advances to the next slide after each Apply/Reject.
  const decidable = review === 'proposal' || review === 'create';
  const rows = queue ?? [];
  const qi = decidable ? rows.findIndex((r) => r.slideId === row.slideId) : -1;
  const stepper = qi >= 0 && rows.length > 1;
  const jumpTo = (r: ActivityRow | null | undefined) => r?.slideId && onJumpToSlide?.(r.slideId);

  // Only while the candidate is the version on screen: flipped to Before, the
  // overflow you'd be reading about is the old slide's, not the proposal's.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const d = e?.data;
      if (!d || d.type !== 'rkh-slide-overflow') return;
      setOverflow(d.active ? { detail: String(d.detail || '').slice(0, 100), severity: d.severity } : null);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useEffect(() => setOverflow(null), [row.slideId, row.toolId, row.historyId]);

  if (!review) return null;

  async function run(fn: () => Promise<unknown>, { advance = false }: { advance?: boolean } = {}) {
    setBusy(true);
    try {
      await fn();
      onChanged?.();
      // Decision made on a queued proposal — walk to the next one so batch
      // review flows slide to slide without hunting the filmstrip.
      if (advance && qi >= 0) jumpTo(rows[qi + 1] || (qi > 0 ? rows[qi - 1] : null));
    } catch {
      /* the Activity row surfaces errors; the banner stays quiet */
    } finally {
      setBusy(false);
    }
  }

  // toggleRow writes the shared store; every surface follows from there.
  const flip = () => run(() => toggleRow(slug, row));

  // The pills only change WHAT YOU'RE LOOKING AT; the buttons are named after
  // the OUTCOME and never change with the flip. Flipping to "Before" used to
  // leave the version you wanted on screen behind a button called "Reject" —
  // the toggle read as part of the decision when it is only a viewer.
  const wording = {
    proposal: { label: 'AI edit', current: 'Before', candidate: 'After', no: 'Discard change', yes: 'Keep change' },
    create: { label: 'New slide', current: null, candidate: null, no: 'Discard slide', yes: 'Keep slide' },
    revert: {
      label: 'Reviewing revert',
      current: 'Now',
      candidate: 'Before',
      no: 'Cancel',
      yes: 'Restore old version',
    },
    reapply: {
      label: 'Reviewing re-apply',
      current: 'Now',
      candidate: 'Restored',
      no: 'Cancel',
      yes: 'Restore this version',
    },
  }[review];

  // Discarding a pending create takes the slide back out of the deck; the
  // server does that on reject, so both proposal kinds share the same call.
  const decline = () =>
    run(() => (decidable ? rejectRow(slug, row) : cancelRowPreview(slug, row)), { advance: decidable });
  const accept = () =>
    run(() => (decidable ? applyRow(slug, row) : confirmRevertRow(slug, row, { reapply: review === 'reapply' })), {
      advance: decidable,
    });

  return (
    <div className="proposal-banner" role="status" aria-label={wording.label}>
      <span className="proposal-banner__dot" aria-hidden="true" />
      <span className="proposal-banner__label">{wording.label}</span>
      {stepper && (
        <span className="proposal-banner__stepper">
          <button
            type="button"
            className="iconbtn proposal-banner__step"
            onClick={() => jumpTo(rows[qi - 1])}
            disabled={busy || qi === 0}
            title="Previous proposal"
            aria-label="Previous proposal"
          >
            <ChevronLeft />
          </button>
          <span className="proposal-banner__count">
            {qi + 1} of {rows.length}
          </span>
          <button
            type="button"
            className="iconbtn proposal-banner__step"
            onClick={() => jumpTo(rows[qi + 1])}
            disabled={busy || qi === rows.length - 1}
            title="Next proposal"
            aria-label="Next proposal"
          >
            <ChevronRight />
          </button>
        </span>
      )}
      {/* No flip on a new slide: the only alternative is its absence. */}
      {wording.current && (
        <>
          <span className="proposal-banner__viewing">Viewing</span>
          <FlipPills
            showing={showing}
            currentLabel={wording.current}
            candidateLabel={wording.candidate}
            onToggle={flip}
            disabled={busy}
          />
        </>
      )}
      {/* The third option, and only when it applies: the slide on screen is a
          proposal you could keep, and it doesn't fit. */}
      {onTighten && decidable && showing === 'candidate' && overflow && (
        <button
          type="button"
          className="btn btn--ghost btn--sm proposal-banner__tighten"
          onClick={() => onTighten(overflow.detail)}
          disabled={busy}
          title={`This slide's content doesn't fit the frame (${overflow.detail || 'overflowing'}). Let Studio shrink or trim it.`}
        >
          Tighten to fit
        </button>
      )}
      <button type="button" className="btn btn--ghost btn--sm" onClick={decline} disabled={busy}>
        {wording.no}
      </button>
      <button type="button" className="btn btn--primary btn--sm" onClick={accept} disabled={busy}>
        {wording.yes}
      </button>
    </div>
  );
}
